import React, { useState } from 'react';
import {
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  BarChart,
  Bar,
  Legend
} from 'recharts';
import './ProgressChart.css';

/**
 * ProgressChart - Displays skill proficiency and progress visualizations
 * 
 * Props:
 * - skillData: Array of {skill, proficiency, category} for radar chart
 * - trendData: Array of {date, score} for area chart
 * - recentAssessments: Array of {name, score, date} for bar chart
 * - view: 'skills' | 'timeline' | 'recent' - which chart to show
 */
const ProgressChart = ({ 
  skillData = [], 
  trendData = [], 
  recentAssessments = [],
  view = 'skills',
  title = 'Progress Overview'
}) => {
  const [activeView, setActiveView] = useState(view);

  // Format skill data for radar chart
  const formatSkillData = (data) => {
    return data.map(item => ({
      skill: item.skill_name || item.skill || item.name,
      proficiency: item.proficiency_score || item.proficiency || item.score || 0,
      fullMark: 100
    }));
  };

  // Format trend data for area chart
  const formatTrendData = (data) => {
    return data.map(item => ({
      date: new Date(item.date || item.completed_at).toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric' 
      }),
      score: item.score || item.percentage || 0
    }));
  };

  // Format recent assessments for bar chart
  const formatRecentData = (data) => {
    return data.slice(0, 8).map(item => ({
      name: (item.title || item.name || 'Assessment').substring(0, 15) + 
            ((item.title || item.name || '').length > 15 ? '...' : ''),
      score: item.score || item.percentage || 0,
      passingScore: item.passing_score || 70
    }));
  };

  const renderSkillsRadar = () => {
    const formattedData = formatSkillData(skillData);
    
    if (formattedData.length === 0) {
      return (
        <div className="chart-empty">
          <p>No skill data available yet.</p>
          <p className="chart-empty-hint">Complete assessments to see your skill proficiency.</p>
        </div>
      );
    }

    return (
      <ResponsiveContainer width="100%" height={350}>
        <RadarChart data={formattedData} margin={{ top: 20, right: 30, bottom: 20, left: 30 }}>
          <PolarGrid stroke="var(--border-color, #e0e0e0)" />
          <PolarAngleAxis 
            dataKey="skill" 
            tick={{ fill: 'var(--text-secondary, #666)', fontSize: 12 }}
          />
          <PolarRadiusAxis 
            angle={90} 
            domain={[0, 100]} 
            tick={{ fill: 'var(--text-secondary, #666)', fontSize: 10 }}
          />
          <Radar
            name="Proficiency"
            dataKey="proficiency"
            stroke="var(--primary-color, #6366f1)"
            fill="var(--primary-color, #6366f1)"
            fillOpacity={0.4}
            strokeWidth={2}
          />
          <Tooltip 
            contentStyle={{ 
              backgroundColor: 'var(--card-bg, #fff)',
              border: '1px solid var(--border-color, #e0e0e0)',
              borderRadius: '8px'
            }}
            formatter={(value) => [`${value}%`, 'Proficiency']}
          />
        </RadarChart>
      </ResponsiveContainer>
    );
  };

  const renderTrendChart = () => {
    const formattedData = formatTrendData(trendData);
    
    if (formattedData.length === 0) {
      return (
        <div className="chart-empty">
          <p>No trend data available yet.</p>
          <p className="chart-empty-hint">Complete more assessments to see your progress over time.</p>
        </div>
      );
    }

    return (
      <ResponsiveContainer width="100%" height={350}>
        <AreaChart data={formattedData} margin={{ top: 20, right: 30, bottom: 20, left: 0 }}>
          <defs>
            <linearGradient id="scoreGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="var(--primary-color, #6366f1)" stopOpacity={0.4}/>
              <stop offset="95%" stopColor="var(--primary-color, #6366f1)" stopOpacity={0}/>
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color, #e0e0e0)" />
          <XAxis 
            dataKey="date" 
            tick={{ fill: 'var(--text-secondary, #666)', fontSize: 12 }}
          />
          <YAxis 
            domain={[0, 100]}
            tick={{ fill: 'var(--text-secondary, #666)', fontSize: 12 }}
            tickFormatter={(value) => `${value}%`}
          />
          <Tooltip 
            contentStyle={{ 
              backgroundColor: 'var(--card-bg, #fff)',
              border: '1px solid var(--border-color, #e0e0e0)',
              borderRadius: '8px'
            }}
            formatter={(value) => [`${value}%`, 'Score']}
          />
          <Area
            type="monotone"
            dataKey="score"
            stroke="var(--primary-color, #6366f1)"
            fill="url(#scoreGradient)"
            strokeWidth={2}
          />
        </AreaChart>
      </ResponsiveContainer>
    );
  };

  const renderRecentChart = () => {
    const formattedData = formatRecentData(recentAssessments);
    
    if (formattedData.length === 0) {
      return (
        <div className="chart-empty">
          <p>No recent assessments.</p>
          <p className="chart-empty-hint">Take an assessment to see your results here.</p>
        </div>
      );
    }

    return (
      <ResponsiveContainer width="100%" height={350}>
        <BarChart data={formattedData} margin={{ top: 20, right: 30, bottom: 60, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color, #e0e0e0)" />
          <XAxis 
            dataKey="name" 
            tick={{ fill: 'var(--text-secondary, #666)', fontSize: 11 }}
            angle={-45}
            textAnchor="end"
            height={60}
          />
          <YAxis 
            domain={[0, 100]}
            tick={{ fill: 'var(--text-secondary, #666)', fontSize: 12 }}
            tickFormatter={(value) => `${value}%`}
          />
          <Tooltip 
            contentStyle={{ 
              backgroundColor: 'var(--card-bg, #fff)',
              border: '1px solid var(--border-color, #e0e0e0)',
              borderRadius: '8px'
            }}
            formatter={(value, name) => [`${value}%`, name === 'score' ? 'Your Score' : 'Passing Score']}
          />
          <Legend />
          <Bar 
            dataKey="score" 
            name="Your Score"
            fill="var(--primary-color, #6366f1)" 
            radius={[4, 4, 0, 0]}
          />
          <Bar 
            dataKey="passingScore" 
            name="Passing Score"
            fill="var(--success-color, #10b981)" 
            radius={[4, 4, 0, 0]}
            fillOpacity={0.3}
          />
        </BarChart>
      </ResponsiveContainer>
    );
  };

  const renderChart = () => {
    switch (activeView) {
      case 'skills':
        return renderSkillsRadar();
      case 'timeline':
        return renderTrendChart();
      case 'recent':
        return renderRecentChart();
      default:
        return renderSkillsRadar();
    }
  };

  return (
    <div className="progress-chart">
      <div className="progress-chart-header">
        <h3 className="progress-chart-title">{title}</h3>
        <div className="chart-view-toggle">
          <button 
            className={`view-btn ${activeView === 'skills' ? 'active' : ''}`}
            onClick={() => setActiveView('skills')}
          >
            Skills
          </button>
          <button 
            className={`view-btn ${activeView === 'timeline' ? 'active' : ''}`}
            onClick={() => setActiveView('timeline')}
          >
            Timeline
          </button>
          <button 
            className={`view-btn ${activeView === 'recent' ? 'active' : ''}`}
            onClick={() => setActiveView('recent')}
          >
            Recent
          </button>
        </div>
      </div>
      <div className="progress-chart-content">
        {renderChart()}
      </div>
    </div>
  );
};

export default ProgressChart;
